import { Injectable } from '@angular/core';
import {
	Attr,
	AttrPath,
	BovenbouwsuccesUitzondering,
	CategorieUitstroomniveauVSO,
	DoorstroomExt,
	DoorstroomResultaat,
	DoorstroomResultaatMetPrognose,
	ExamencijfersUitzondering,
	Examenstatus,
	Instroom,
	Niveau,
	OnderbouwsnelheidUitzondering,
	OnderwijspositieUitzondering,
	Overgangmoment,
	TussentijdseUitstroomVSO,
	Uitstroom,
	VergelijkingUitstroomniveauVSO,
} from '@cumlaude/metadata';
import { get } from 'lodash-es';
import {
	AbsentieKlasse,
	AbsentieKlasseHistorie,
	AbsoluutUitstroomniveauVerschil,
	AfgeslotenLes,
	Afwezigheid,
	AfwijkingNegatiefPositief,
	AlternatieveNormering,
	APCG,
	Bekostigd,
	Bevorderd,
	Cijfer,
	CijferOV,
	CijferOVAdvies,
	CijferVerschil,
	CohortrendementTotDoublure,
	CohortrendementTotNiveau,
	CohortrendementTotWaarde,
	CohortrendementVanafDoublure,
	CohortrendementVanafNiveau,
	CohortrendementVanafWaarde,
	CohortstatusTot,
	DefaultBooleanEnum,
	Diplomavak,
	ExamenKandidaat,
	ExamenOpleiding,
	ExamenOpstroom,
	Extraneus,
	Geoorloofd,
	InExamendossier,
	Interventielaag,
	InVoortgangsdossier,
	Is1AprilPlaatsing,
	Is1AugPlaatsing,
	Is1DecPlaatsing,
	Is1FebrPlaatsing,
	Is1JanPlaatsing,
	Is1JuliPlaatsing,
	Is1JuniPlaatsing,
	Is1MeiPlaatsing,
	Is1MrtPlaatsing,
	Is1NovPlaatsing,
	Is1OktPlaatsing,
	Is1SeptPlaatsing,
	LaatstePlaatsingSchooljaar,
	Leerwerktraject,
	Lesregistratie,
	LWOO,
	MetZonderCE,
	Nieuwkomer,
	NiveauVerschil,
	Ondersteunend,
	OnderwijspositiePunten,
	OnderwijsresultatenActueel,
	OnderwijsresultatenHistorie,
	Onderwijzend,
	Overgang,
	PercentielNegatiefGemiddeldPositief,
	PlaatsingActueel,
	PlaatsingStatus,
	PresentieregistratieVerplicht,
	PrestatieanalyseHistorie,
	Prognose,
	ReferentieNiveau,
	Succesvol,
	TeltMeeVoorSlagen,
	UitstroomniveauVerschil,
	UitstroomniveauWaarde,
	VakantieMidden,
	VakantieNoord,
	VakantieZuid,
	VAVO,
	Voldoende,
	Weekend,
} from '../../services/label-enums';

type EnumObject = { [p: string]: string };

type EnumMap = {
	[K in Attr]?: EnumObject | EnumMap;
};

export type LegendaEnumNames =
	| 'x_met_overgang'
	| 'x_onderwijspositie_punten'
	| 'x_cijfer_ov'
	| 'x_cijfer_ov_advies'
	| 'x_cijfer_metzonder_ce'
	| 'x_afwijking_neg_pos'
	| 'x_aw_is_abs_geoorloofd_historie'
	| 'x_onderwijsresultaten_actueel'
	| 'x_onderwijsresultaten_historie'
	| 'x_percentiel_neg_gem_pos'
	| 'x_prestatieanalyse_historie';

type LegendaEnumMap = {
	[K in Attr | LegendaEnumNames]?: EnumObject | LegendaEnumMap;
};

export function getAbsentieKlasse(isGeoorloofd: string | null): AbsentieKlasse {
	switch (isGeoorloofd) {
		case '0':
			return AbsentieKlasse.Ongeoorloofd;
		case '1':
			return AbsentieKlasse.Geoorloofd;
		default:
			throw new Error(`aw_is_abs_geoorloofd ${isGeoorloofd}, zou niet voor mogen komen.`);
	}
}

const iltEnumMap: EnumMap = {
	ilt_is_lwoo: LWOO,
	ilt_is_leerwerktraject: Leerwerktraject,
	ilt_is_vavo: VAVO,

	ilt_nm_niveau: Niveau,
};

const loopbaanEnumMap: EnumMap = {
	lb_is_1febr_plaatsing: Is1FebrPlaatsing,
	lb_is_1okt_plaatsing: Is1OktPlaatsing,
	lb_is_apcg: APCG,
	lb_is_bekostigd: Bekostigd,
	lb_is_bevorderd: Bevorderd,
	lb_is_examenkandidaat: ExamenKandidaat,
	lb_is_examenopleiding: ExamenOpleiding,
	lb_is_examenopstroom: ExamenOpstroom,
	lb_is_extraneus: Extraneus,
	lb_is_laatste_plaatsing_sj: LaatstePlaatsingSchooljaar,
	lb_is_nieuwkomer_2jaar: Nieuwkomer,
	lb_is_nieuwkomer_4jaar: Nieuwkomer,
	lb_is_pl_voorlopig: PlaatsingStatus,
	lb_is_succesvol: Succesvol,
};

const cijferEnumMap: EnumMap = {
	cf_fk_ilt: iltEnumMap,
	cf_fk_ilt_volgend_sj: iltEnumMap,
	cf_fk_ilt_vorig_sj: iltEnumMap,
	cf_fk_lb: loopbaanEnumMap,
	cf_fk_lb_volgend_sj: loopbaanEnumMap,
	cf_fk_lb_vorig_sj: loopbaanEnumMap,

	cf_fun_is_plaatsing_peildatum_1april: Is1AprilPlaatsing,
	cf_fun_is_plaatsing_peildatum_1aug: Is1AugPlaatsing,
	cf_fun_is_plaatsing_peildatum_1dec: Is1DecPlaatsing,
	cf_fun_is_plaatsing_peildatum_1feb: Is1FebrPlaatsing,
	cf_fun_is_plaatsing_peildatum_1jan: Is1JanPlaatsing,
	cf_fun_is_plaatsing_peildatum_1juli: Is1JuliPlaatsing,
	cf_fun_is_plaatsing_peildatum_1juni: Is1JuniPlaatsing,
	cf_fun_is_plaatsing_peildatum_1mei: Is1MeiPlaatsing,
	cf_fun_is_plaatsing_peildatum_1mrt: Is1MrtPlaatsing,
	cf_fun_is_plaatsing_peildatum_1nov: Is1NovPlaatsing,
	cf_fun_is_plaatsing_peildatum_1okt: Is1OktPlaatsing,
	cf_fun_is_plaatsing_peildatum_1sep: Is1SeptPlaatsing,

	cf_fun_nm_idu: DoorstroomResultaatMetPrognose,

	cf_is_alternatievenormering: AlternatieveNormering,
	cf_is_diplomavak: Diplomavak,
	cf_is_examendossier: InExamendossier,
	cf_is_telt_mee_voor_slagen: TeltMeeVoorSlagen,
	cf_is_voldoende: Voldoende,
	cf_is_voortgangsdossier: InVoortgangsdossier,

	cf_nm_excijf_uitzondering: ExamencijfersUitzondering,

	cf_nr_cijfer_afgerond: Cijfer,
};

const doorstroomEnumMap: EnumMap = {
	ds_fk_ilt_naar: iltEnumMap,
	ds_fk_ilt_van: iltEnumMap,
	ds_fk_ilt_vorig_sj: iltEnumMap,
	ds_fk_lb_naar: loopbaanEnumMap,
	ds_fk_lb_van: loopbaanEnumMap,
	ds_fk_lb_vorig_sj: loopbaanEnumMap,

	ds_fun_nm_doorstroom_ext: DoorstroomExt,
	ds_fun_is_plaatsing_actueel: PlaatsingActueel,
	ds_fun_is_plaatsing_peildatum_1april: Is1AprilPlaatsing,
	ds_fun_is_plaatsing_peildatum_1aug: Is1AugPlaatsing,
	ds_fun_is_plaatsing_peildatum_1dec: Is1DecPlaatsing,
	ds_fun_is_plaatsing_peildatum_1feb: Is1FebrPlaatsing,
	ds_fun_is_plaatsing_peildatum_1jan: Is1JanPlaatsing,
	ds_fun_is_plaatsing_peildatum_1juli: Is1JuliPlaatsing,
	ds_fun_is_plaatsing_peildatum_1juni: Is1JuniPlaatsing,
	ds_fun_is_plaatsing_peildatum_1mei: Is1MeiPlaatsing,
	ds_fun_is_plaatsing_peildatum_1mrt: Is1MrtPlaatsing,
	ds_fun_is_plaatsing_peildatum_1nov: Is1NovPlaatsing,
	ds_fun_is_plaatsing_peildatum_1okt: Is1OktPlaatsing,
	ds_fun_is_plaatsing_peildatum_1sep: Is1SeptPlaatsing,
	ds_fun_nm_overgangmoment: Overgangmoment,

	ds_is_bevorderd: Bevorderd,
	ds_is_prognose: Prognose,
	ds_is_succesvol: Succesvol,

	ds_fun_nm_examenstatus: Examenstatus,

	ds_nm_bbs_uitzondering_van: BovenbouwsuccesUitzondering,
	ds_nm_idu: DoorstroomResultaat,
	ds_nm_instroom_in_schooljaar: Instroom,
	ds_nm_instroom_van: Instroom,
	ds_nm_obs_uitzondering_van: OnderbouwsnelheidUitzondering,
	ds_nm_op_uitzondering_van: OnderwijspositieUitzondering,
	ds_nm_uitstroom: Uitstroom,
	ds_nm_uitstroom_in_schooljaar: Uitstroom,
};

const periodeEnumMap: EnumMap = {
	per_is_vakantie_midden: VakantieMidden,
	per_is_vakantie_noord: VakantieNoord,
	per_is_vakantie_zuid: VakantieZuid,
	per_is_weekend: Weekend,
};

const lesregistratieEnumMap: EnumMap = {
	lr_d_datum: periodeEnumMap,

	lr_fk_ilt: iltEnumMap,
	lr_fk_ilt_volgend_sj: iltEnumMap,
	lr_fk_ilt_vorig_sj: iltEnumMap,
	lr_fk_lb: loopbaanEnumMap,
	lr_fk_lb_volgend_sj: loopbaanEnumMap,
	lr_fk_lb_vorig_sj: loopbaanEnumMap,

	lr_fun_is_plaatsing_peildatum_1april: Is1AprilPlaatsing,
	lr_fun_is_plaatsing_peildatum_1aug: Is1AugPlaatsing,
	lr_fun_is_plaatsing_peildatum_1dec: Is1DecPlaatsing,
	lr_fun_is_plaatsing_peildatum_1feb: Is1FebrPlaatsing,
	lr_fun_is_plaatsing_peildatum_1jan: Is1JanPlaatsing,
	lr_fun_is_plaatsing_peildatum_1juli: Is1JuliPlaatsing,
	lr_fun_is_plaatsing_peildatum_1juni: Is1JuniPlaatsing,
	lr_fun_is_plaatsing_peildatum_1mei: Is1MeiPlaatsing,
	lr_fun_is_plaatsing_peildatum_1mrt: Is1MrtPlaatsing,
	lr_fun_is_plaatsing_peildatum_1nov: Is1NovPlaatsing,
	lr_fun_is_plaatsing_peildatum_1okt: Is1OktPlaatsing,
	lr_fun_is_plaatsing_peildatum_1sep: Is1SeptPlaatsing,

	lr_is_geoorloofd: Geoorloofd,

	lr_nm_lesregistratie: Lesregistratie,
};

const aanwezigheidEnumMap: EnumMap = {
	aw_d_datum: periodeEnumMap,

	aw_fk_ilt: iltEnumMap,
	aw_fk_ilt_volgend_sj: iltEnumMap,
	aw_fk_ilt_vorig_sj: iltEnumMap,
	aw_fk_lb: loopbaanEnumMap,
	aw_fk_lb_volgend_sj: loopbaanEnumMap,
	aw_fk_lb_vorig_sj: loopbaanEnumMap,

	aw_fun_is_plaatsing_peildatum_1april: Is1AprilPlaatsing,
	aw_fun_is_plaatsing_peildatum_1aug: Is1AugPlaatsing,
	aw_fun_is_plaatsing_peildatum_1dec: Is1DecPlaatsing,
	aw_fun_is_plaatsing_peildatum_1feb: Is1FebrPlaatsing,
	aw_fun_is_plaatsing_peildatum_1jan: Is1JanPlaatsing,
	aw_fun_is_plaatsing_peildatum_1juli: Is1JuliPlaatsing,
	aw_fun_is_plaatsing_peildatum_1juni: Is1JuniPlaatsing,
	aw_fun_is_plaatsing_peildatum_1mei: Is1MeiPlaatsing,
	aw_fun_is_plaatsing_peildatum_1mrt: Is1MrtPlaatsing,
	aw_fun_is_plaatsing_peildatum_1nov: Is1NovPlaatsing,
	aw_fun_is_plaatsing_peildatum_1okt: Is1OktPlaatsing,
	aw_fun_is_plaatsing_peildatum_1sep: Is1SeptPlaatsing,
	aw_fun_is_presentieregistratie_verplicht_afspraak: PresentieregistratieVerplicht,

	aw_is_abs_geoorloofd: Geoorloofd,
	aw_is_absent: Afwezigheid,
	aw_is_presentieregistratie_verplicht: PresentieregistratieVerplicht,
};

const basisvaardighedenEnumMap: EnumMap = {
	bv_fk_ilt: iltEnumMap,
	bv_fk_ilt_volgend_sj: iltEnumMap,
	bv_fk_ilt_vorig_sj: iltEnumMap,
	bv_fk_lb: loopbaanEnumMap,
	bv_fk_lb_volgend_sj: loopbaanEnumMap,
	bv_fk_lb_vorig_sj: loopbaanEnumMap,

	bv_fun_is_plaatsing_peildatum_1april: Is1AprilPlaatsing,
	bv_fun_is_plaatsing_peildatum_1aug: Is1AugPlaatsing,
	bv_fun_is_plaatsing_peildatum_1dec: Is1DecPlaatsing,
	bv_fun_is_plaatsing_peildatum_1feb: Is1FebrPlaatsing,
	bv_fun_is_plaatsing_peildatum_1jan: Is1JanPlaatsing,
	bv_fun_is_plaatsing_peildatum_1juli: Is1JuliPlaatsing,
	bv_fun_is_plaatsing_peildatum_1juni: Is1JuniPlaatsing,
	bv_fun_is_plaatsing_peildatum_1mei: Is1MeiPlaatsing,
	bv_fun_is_plaatsing_peildatum_1mrt: Is1MrtPlaatsing,
	bv_fun_is_plaatsing_peildatum_1nov: Is1NovPlaatsing,
	bv_fun_is_plaatsing_peildatum_1okt: Is1OktPlaatsing,
	bv_fun_is_plaatsing_peildatum_1sep: Is1SeptPlaatsing,
};

const vakkeuzeEnumMap: EnumMap = {
	vkk_fk_ilt: iltEnumMap,
	vkk_fk_ilt_volgend_sj: iltEnumMap,
	vkk_fk_ilt_vorig_sj: iltEnumMap,
	vkk_fk_lb: loopbaanEnumMap,
	vkk_fk_lb_volgend_sj: loopbaanEnumMap,
	vkk_fk_lb_vorig_sj: loopbaanEnumMap,

	vkk_fun_is_plaatsing_peildatum_1april: Is1AprilPlaatsing,
	vkk_fun_is_plaatsing_peildatum_1aug: Is1AugPlaatsing,
	vkk_fun_is_plaatsing_peildatum_1dec: Is1DecPlaatsing,
	vkk_fun_is_plaatsing_peildatum_1feb: Is1FebrPlaatsing,
	vkk_fun_is_plaatsing_peildatum_1jan: Is1JanPlaatsing,
	vkk_fun_is_plaatsing_peildatum_1juli: Is1JuliPlaatsing,
	vkk_fun_is_plaatsing_peildatum_1juni: Is1JuniPlaatsing,
	vkk_fun_is_plaatsing_peildatum_1mei: Is1MeiPlaatsing,
	vkk_fun_is_plaatsing_peildatum_1mrt: Is1MrtPlaatsing,
	vkk_fun_is_plaatsing_peildatum_1nov: Is1NovPlaatsing,
	vkk_fun_is_plaatsing_peildatum_1okt: Is1OktPlaatsing,
	vkk_fun_is_plaatsing_peildatum_1sep: Is1SeptPlaatsing,
};

const medewerkerEnumMap: EnumMap = {};

const enumMap: EnumMap = {
	...aanwezigheidEnumMap,
	...basisvaardighedenEnumMap,
	...cijferEnumMap,
	...doorstroomEnumMap,
	...lesregistratieEnumMap,
	...medewerkerEnumMap,
	...vakkeuzeEnumMap,
};

const iltLegendaEnumMap: LegendaEnumMap = {
	...iltEnumMap,

	ilt_is_lwoo: LWOO,
	ilt_is_vavo: VAVO,
};

const leerlingLegendaEnumMap: LegendaEnumMap = {
	ll_is_cohortstatus_tot_volledig: CohortstatusTot,
};

const medewerkerLegendaEnumMap: LegendaEnumMap = {
	...medewerkerEnumMap,

	mw_is_ondersteunend: Ondersteunend,
	mw_is_onderwijzend: Onderwijzend,
};

const cijferLegendaEnumMap: LegendaEnumMap = {
	...cijferEnumMap,

	cf_fk_ilt: iltLegendaEnumMap,
	cf_fk_ilt_volgend_sj: iltLegendaEnumMap,
	cf_fk_ilt_vorig_sj: iltLegendaEnumMap,

	cf_fks_mw: medewerkerLegendaEnumMap,
	cf_fun_nr_cijferverschil_se_ce_afgerond: CijferVerschil,

	x_afwijking_neg_pos: AfwijkingNegatiefPositief,
	x_cijfer_metzonder_ce: MetZonderCE,
	x_cijfer_ov: CijferOV,
	x_cijfer_ov_advies: CijferOVAdvies,
};

const loopbaanLegendaEnumMap: LegendaEnumMap = {
	...loopbaanEnumMap,

	lb_nr_beoogd_uitstroomniveau_vso: UitstroomniveauWaarde,
	lb_nr_uitstroomniveau_vso: UitstroomniveauWaarde,
};

const doorstroomLegendaEnumMap: LegendaEnumMap = {
	...doorstroomEnumMap,

	ds_fk_ilt_naar: iltLegendaEnumMap,
	ds_fk_ilt_van: iltLegendaEnumMap,
	ds_fk_ilt_vorig_sj: iltLegendaEnumMap,
	ds_fk_lb_van: loopbaanLegendaEnumMap,
	ds_fk_ll: leerlingLegendaEnumMap,

	ds_fun_nr_abs_verschil_einduitstroomniveau_vso: AbsoluutUitstroomniveauVerschil,
	ds_fun_nm_categorie_uitstroomniveau_vso: CategorieUitstroomniveauVSO,
	ds_fun_nm_vergelijk_einduitstroomniveau_vso: VergelijkingUitstroomniveauVSO,
	ds_fun_co_verschil_basisschooladvies_van: NiveauVerschil,
	ds_fun_nr_verschil_einduitstroomniveau_vso: UitstroomniveauVerschil,

	ds_is_apcg_van: APCG,
	ds_is_examenopstroom_van: ExamenOpstroom,
	ds_is_1okt_plaatsing_van: Is1OktPlaatsing,
	ds_is_1febr_plaatsing_van: Is1FebrPlaatsing,
	ds_is_examenkandidaat_van: ExamenKandidaat,
	ds_is_laatste_plaatsing_sj_van: LaatstePlaatsingSchooljaar,

	ds_nm_cohortrendement_tot_cohortrendement: CohortrendementTotWaarde,
	ds_nm_cohortrendement_tot_doublures: CohortrendementTotDoublure,
	ds_nm_cohortrendement_tot_niveauverschil: CohortrendementTotNiveau,
	ds_nm_cohortrendement_vanaf_cohortrendement: CohortrendementVanafWaarde,
	ds_nm_cohortrendement_vanaf_doublures: CohortrendementVanafDoublure,
	ds_nm_cohortrendement_vanaf_niveauverschil: CohortrendementVanafNiveau,
	ds_nm_tussentijdse_uitstroom_vso: TussentijdseUitstroomVSO,

	x_met_overgang: Overgang,

	x_onderwijspositie_punten: OnderwijspositiePunten,
};

const aanwezigheidLegendaEnumMap: LegendaEnumMap = {
	...aanwezigheidEnumMap,

	aw_fk_ilt: iltLegendaEnumMap,
	aw_fk_ilt_volgend_sj: iltLegendaEnumMap,
	aw_fk_ilt_vorig_sj: iltLegendaEnumMap,
	aw_fk_ll: leerlingLegendaEnumMap,

	aw_is_abs_geoorloofd: AbsentieKlasse,
	aw_is_presentieregistratie_verwerkt: AfgeslotenLes,

	aw_fun_nm_interventielaag: Interventielaag,

	x_aw_is_abs_geoorloofd_historie: AbsentieKlasseHistorie,
};

const basisvaardighedenLegendaEnumMap: LegendaEnumMap = {
	...basisvaardighedenEnumMap,

	bv_fk_ilt: iltLegendaEnumMap,
	bv_fk_ilt_volgend_sj: iltLegendaEnumMap,
	bv_fk_ilt_vorig_sj: iltLegendaEnumMap,
	bv_fk_ll: leerlingLegendaEnumMap,

	bv_fun_nr_referentieniveau_bin: ReferentieNiveau,
};

const lesregistratieLegendaEnumMap: LegendaEnumMap = {
	...lesregistratieEnumMap,

	lr_fk_ilt: iltLegendaEnumMap,
	lr_fk_ilt_volgend_sj: iltLegendaEnumMap,
	lr_fk_ilt_vorig_sj: iltLegendaEnumMap,
	lr_fk_ll: leerlingLegendaEnumMap,
};

const vakkeuzeLegendaEnumMap: LegendaEnumMap = {
	...vakkeuzeEnumMap,

	vkk_fk_ilt: iltLegendaEnumMap,
	vkk_fk_ilt_volgend_sj: iltLegendaEnumMap,
	vkk_fk_ilt_vorig_sj: iltLegendaEnumMap,
	vkk_fk_ll: leerlingLegendaEnumMap,
};

const legendaEnumMap: LegendaEnumMap = {
	...enumMap,
	...aanwezigheidLegendaEnumMap,
	...basisvaardighedenLegendaEnumMap,
	...cijferLegendaEnumMap,
	...doorstroomLegendaEnumMap,
	...lesregistratieLegendaEnumMap,
	...medewerkerLegendaEnumMap,
	...vakkeuzeLegendaEnumMap,

	x_onderwijsresultaten_actueel: OnderwijsresultatenActueel,
	x_onderwijsresultaten_historie: OnderwijsresultatenHistorie,

	x_percentiel_neg_gem_pos: PercentielNegatiefGemiddeldPositief,

	x_prestatieanalyse_historie: PrestatieanalyseHistorie,
};

export type Entry = {
	key: string | null;
	value: string;
};

@Injectable({
	providedIn: 'root',
})
export class EnumService {
	/**
	 * Geeft de lijst met mogelijke enum waardes terug.
	 *
	 * @param attrPath waarop gezocht moet worden.
	 */
	static enumValues(attrPath: AttrPath): string[] {
		const e = get(enumMap, attrPath);
		return e ? Object.values(e) : [];
	}

	/**
	 * Geeft de lijst met mogelijke enum waardes terug.
	 * Deze functie doet voor enums van string attributen hetzelfde als {@link enumValues}.
	 * Daarnaast voor boolean attributen geeft deze de enum waardes die beschrijven wat de boolean opties betekenen.
	 *
	 * @param attrPath waarop gezocht moet worden.
	 */
	static legendaEnumEntries(attrPath: AttrPath | [LegendaEnumNames]): Entry[] {
		const enumObj = get(legendaEnumMap, attrPath) ?? {};
		const entries = Object.entries<string>(enumObj);
		return entries.map(([key, value]) => ({
			key,
			value,
		}));
	}

	/**
	 * Geeft de enum waarde die hoort bij de meegegeven combinatie.
	 * Roept daarbij de {@link legendaEnumEntries} functie aan.
	 * Gaat daarbij vanuit dat de true waarde als eerste komt en de false waarde als tweede.
	 *
	 * @param attrPath waarop gezocht moet worden
	 * @param value is de waarde waarvan de enum waarde van opgezocht moet worden.
	 */
	static booleanEnumEntry(attrPath: AttrPath | [LegendaEnumNames], value: boolean) {
		const entries = this.legendaEnumEntries(attrPath);
		const index = value ? 0 : 1;
		return index < entries.length ? entries[index].value : Object.values(DefaultBooleanEnum)[index];
	}
}
